/**
 * QuickReviewDraftDialog — Modale de révision rapide d'un brouillon issu de
 * l'import PDF en masse (typiquement les factures pour lesquelles l'OCR n'a
 * pas pu extraire le nom du client). L'utilisateur peut compléter en 3 clics
 * les 3 champs essentiels : nom du client, description, montant HT — sans
 * naviguer vers l'éditeur complet.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function QuickReviewDraftDialog({
  invoiceId, open, onOpenChange, onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inv, setInv] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [description, setDescription] = useState("");
  const [amountHt, setAmountHt] = useState("");
  const [vatRate, setVatRate] = useState(20);

  useEffect(() => {
    if (!open || !invoiceId) return;
    setLoading(true);
    api.get(`/invoices/${invoiceId}`)
      .then(({ data }) => {
        const i = data.invoice || data;
        setInv(i);
        setClientName(i?.client?.name === "Client à identifier" ? "" : (i?.client?.name || ""));
        setClientEmail(i?.client?.email || "");
        const line0 = (i?.lines || [])[0] || {};
        setDescription(line0.description || "");
        setAmountHt(String(Number(line0.unit_price_ht) * Number(line0.quantity || 1) || ""));
        setVatRate(Number(line0.vat_rate ?? 20));
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, invoiceId]);

  const save = async () => {
    if (!clientName.trim()) {
      toast.error("Nom du client requis");
      return;
    }
    const amt = Number(amountHt);
    if (!(amt > 0)) {
      toast.error("Montant HT invalide");
      return;
    }
    setSaving(true);
    try {
      // Patch : on remplace client + une seule ligne récap. L'utilisateur
      // pourra affiner (multi-lignes, remises, etc.) plus tard dans l'éditeur
      // complet s'il le souhaite.
      await api.patch(`/invoices/${invoiceId}`, {
        client: {
          name: clientName.trim(),
          email: clientEmail.trim() || null,
        },
        lines: [{
          description: (description.trim() || "Prestation").slice(0, 300),
          quantity: 1,
          unit_price_ht: amt,
          vat_rate: vatRate,
          discount_percent: 0,
        }],
      });
      toast.success("Brouillon complété");
      onOpenChange(false);
      onSaved?.(invoiceId);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="quick-review-dialog">
        <DialogHeader>
          <DialogTitle>Réviser ce brouillon</DialogTitle>
          <DialogDescription className="text-xs">
            Complète les 3 champs essentiels. Tu pourras affiner les lignes,
            les remises et les mentions dans l'éditeur complet ensuite.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="qr-client-name" className="text-xs uppercase text-slate-500">
                Nom du client *
              </Label>
              <Input
                id="qr-client-name"
                name="qr-client-name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Ex : Cabinet Meunier"
                autoFocus
                data-testid="qr-client-name"
              />
            </div>
            <div>
              <Label htmlFor="qr-client-email" className="text-xs uppercase text-slate-500">
                Email client (facultatif)
              </Label>
              <Input
                id="qr-client-email"
                name="qr-client-email"
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="contact@client.fr"
                data-testid="qr-client-email"
              />
            </div>
            <div>
              <Label htmlFor="qr-description" className="text-xs uppercase text-slate-500">
                Description
              </Label>
              <Input
                id="qr-description"
                name="qr-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex : Prestation conseil janvier 2024"
                data-testid="qr-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="qr-amount-ht" className="text-xs uppercase text-slate-500">
                  Montant HT *
                </Label>
                <Input
                  id="qr-amount-ht"
                  name="qr-amount-ht"
                  type="number"
                  step="0.01"
                  value={amountHt}
                  onChange={(e) => setAmountHt(e.target.value)}
                  placeholder="0.00"
                  className="font-mono text-right"
                  data-testid="qr-amount-ht"
                />
              </div>
              <div>
                <Label htmlFor="qr-vat-rate" className="text-xs uppercase text-slate-500">
                  TVA %
                </Label>
                <select
                  id="qr-vat-rate"
                  name="qr-vat-rate"
                  value={vatRate}
                  onChange={(e) => setVatRate(Number(e.target.value))}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="qr-vat-rate"
                >
                  <option value={0}>0 %</option>
                  <option value={5.5}>5,5 %</option>
                  <option value={10}>10 %</option>
                  <option value={20}>20 %</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {inv?.id && (
            <Button asChild variant="ghost" className="mr-auto text-xs">
              <Link to={`/app/invoices/${inv.id}/edit`}>
                Éditeur complet <ArrowUpRight className="w-3 h-3 ml-1" />
              </Link>
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}
                   disabled={saving} data-testid="qr-cancel">
            Annuler
          </Button>
          <Button onClick={save} disabled={saving || loading}
                   className="bg-slate-900 hover:bg-slate-800 text-white"
                   data-testid="qr-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
